import React, { useState } from "react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
    Button, Divider, Grid, IconButton, InputLabel, Link, List, ListItem, ListItemText, Typography
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useConselhosState } from "../context/conselhos-context";
import { useConselhosController } from "../context/conselhos-controller";
import { IAtaConselhoRequest } from "../../../../model/admin-portal/request/ata-conselho-request";
import { TextInputWithCounter } from "../../../components/text-input-with-counter";
import DefaultUploadFile from "../../../components/default-upload-file";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";

const emptyDraft: IAtaConselhoRequest = { id: 0, descricao: '', dtaReuniao: '', documentoUrl: '' };
const DOCUMENT_EXTENSIONS = ["pdf", "doc", "docx"];

export const ConselhoAtasSection = () => {
    const { dataRequest } = useConselhosState();
    const { saveAta, removeAta } = useConselhosController();
    const { showMessage } = useBaseLayoutController();
    const [draft, setDraft] = useState<IAtaConselhoRequest>(emptyDraft);
    const [editingIndex, setEditingIndex] = useState<number | undefined>(undefined);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleEdit = (index: number) => {
        setDraft(dataRequest.atas[index]);
        setFile(null);
        setEditingIndex(index);
    };

    const handleCancel = () => {
        setDraft(emptyDraft);
        setFile(null);
        setEditingIndex(undefined);
    };

    const handleFileSelected = async (selected: File | null) => {
        setFile(selected);
        if (!selected) return;

        try {
            setUploading(true);
            const result = await AdminPortalAppService.uploadDocumento(selected);
            setDraft({ ...draft, documentoUrl: result.documentoUrl });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao enviar arquivo' });
            setFile(null);
        } finally {
            setUploading(false);
        }
    };

    const handleSave = () => {
        if (!draft.descricao || !draft.dtaReuniao || !draft.documentoUrl) return;
        saveAta(draft, editingIndex);
        handleCancel();
    };

    return (
        <Grid item lg={12} sm={12} xl={12}>
            <Typography variant="subtitle1" fontWeight="bold">Atas</Typography>
            <Divider sx={{ mb: 1 }} />

            {dataRequest.atas.length > 0 && (
                <List dense>
                    {dataRequest.atas.map((ata, index) => (
                        <ListItem
                            key={index}
                            divider
                            secondaryAction={
                                <React.Fragment>
                                    <IconButton size="small" onClick={() => handleEdit(index)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => removeAta(index)}><DeleteIcon fontSize="small" /></IconButton>
                                </React.Fragment>
                            }
                        >
                            <ListItemText
                                primary={ata.descricao}
                                secondary={
                                    <React.Fragment>
                                        {ata.dtaReuniao ? new Date(ata.dtaReuniao).toLocaleDateString('pt-BR') : ''}
                                        {ata.documentoUrl && (<React.Fragment> {' - '}<Link href={ata.documentoUrl} target="_blank" rel="noopener">abrir documento</Link></React.Fragment>)}
                                    </React.Fragment>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item lg={5} sm={6} xl={5}>
                        <InputLabel>Descrição *</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={150}
                            error={false}
                            value={draft.descricao}
                            onChange={(e) => setDraft({ ...draft, descricao: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item lg={3} sm={4} xl={3}>
                        <InputLabel>Data da Reunião *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small" } }}
                            value={draft.dtaReuniao ? dayjs(draft.dtaReuniao) : null}
                            onChange={(value: Dayjs | null) => setDraft({ ...draft, dtaReuniao: value?.toISOString() || '' })}
                        />
                    </Grid>
                    <Grid item lg={12} sm={12} xl={12}>
                        <InputLabel>Arquivo (PDF ou Word) *</InputLabel>
                        <DefaultUploadFile
                            id="input-ata-conselho"
                            file={file}
                            setFileUpload={handleFileSelected}
                            extensions={DOCUMENT_EXTENSIONS}
                            maxFileSizeMb={10}
                            disabled={uploading || !draft.descricao || !draft.dtaReuniao}
                        />
                        {uploading && (
                            <Typography variant="caption" color="text.secondary">Enviando arquivo, aguarde...</Typography>
                        )}
                        {editingIndex !== undefined && draft.documentoUrl && !uploading && (
                            <Typography variant="caption" color="text.secondary">
                                Arquivo atual: <Link href={draft.documentoUrl} target="_blank" rel="noopener">abrir documento</Link> — selecione um novo arquivo para substituir
                            </Typography>
                        )}
                    </Grid>
                    <Grid item lg={4} sm={4} xl={4} sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={editingIndex === undefined ? <AddIcon /> : undefined}
                            onClick={handleSave}
                            disabled={uploading || !draft.descricao || !draft.dtaReuniao || !draft.documentoUrl}
                        >
                            {uploading ? 'Enviando...' : editingIndex === undefined ? 'Adicionar Ata' : 'Salvar Ata'}
                        </Button>
                        {editingIndex !== undefined && (
                            <Button size="small" onClick={handleCancel}>Cancelar</Button>
                        )}
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </Grid>
    );
};
